import React from 'react';
import { Loader2 } from 'lucide-react';

import type { SimulationRow } from './api';
import { BUCKET_STYLES, FAILED_STYLE, UNCLASSIFIED_STYLE, asWhen } from './constants';

/**
 * The left pane: every simulation, with the verdict on each.
 *
 * Three states have to stay visually distinct, because they mean different things to a reviewer:
 * scored (a bucket + score), the classification FAILED (we tried and could not), and never
 * classified (we have not tried). Collapsing the last two would hide real breakage.
 */

interface SimulationListProps {
  simulations: SimulationRow[];
  selectedRef: string | null;
  loading: boolean;
  onSelect: (itemRef: string) => void;
}

const QualityBadge: React.FC<{ row: SimulationRow }> = ({ row }) => {
  const classification = row.classification;

  let style = UNCLASSIFIED_STYLE;
  let label = 'not classified';

  if (classification?.status === 'error') {
    style = FAILED_STYLE;
    label = 'failed';
  } else if (classification?.bucket) {
    style = BUCKET_STYLES[classification.bucket];
    label = classification.score != null
      ? `${classification.bucket} · ${classification.score.toFixed(2)}`
      : classification.bucket;
  }

  return (
    <span
      className={`shrink-0 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10.5px]
                  font-bold uppercase tracking-wide ${style}`}
    >
      {label}
    </span>
  );
};

const SimulationList: React.FC<SimulationListProps> = ({
  simulations, selectedRef, loading, onSelect,
}) => {
  if (loading && simulations.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (simulations.length === 0) {
    return (
      <p className="px-6 py-16 text-center text-sm text-gray-500">
        No items match these filters.
      </p>
    );
  }

  return (
    <ul aria-label="Simulations" className="divide-y divide-gray-200">
      {simulations.map((row) => (
        <li key={row.item_ref}>
          <button
            type="button"
            onClick={() => onSelect(row.item_ref)}
            aria-current={row.item_ref === selectedRef}
            className={`block w-full px-4 py-3 text-left transition-colors ${
              row.item_ref === selectedRef
                ? 'bg-blue-50 shadow-[inset_3px_0_0_theme(colors.blue.500)]'
                : 'hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-gray-900">
                {row.name || (row.kind === 'chat' ? `Chat ${row.id}` : `Simulation ${row.id}`)}
              </span>
              <QualityBadge row={row} />
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11.5px] text-gray-500">
              {/* The kind is what tells a reviewer whether they are about to read a report or a
                  conversation — worth a word, not just a differently-shaped id. */}
              <span className="font-medium text-gray-600">
                {row.kind === 'chat' ? 'Chat' : 'Sim'}
              </span>
              <span aria-hidden="true">·</span>
              <span>#{row.id}</span>
              <span aria-hidden="true">·</span>
              <span className="truncate">{row.use_case || 'no use case'}</span>
              <span aria-hidden="true">·</span>
              <span>{asWhen(row.created_at)}</span>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
};

export default SimulationList;
